<template>
  <!-- $attrs/$listeners are forwarded so the screen can still pass initial-tab
       and listen for tab-change; the HOC used to do that implicitly. -->
  <login-form
    v-bind="$attrs"
    :error="error"
    :loading="loading"
    :invited="invited"
    :pending="pending"
    v-on="$listeners"
    @login="login"
    @signup="signup"
    @resend="resend"
    @reset-signup="resetSignup"
  />
</template>

<script>
import LoginForm from './LoginForm.vue';
import auth from '~lib/common/providers/auth';
import userManagement from '~lib/common/providers/userManagement';
import getSafeRedirectUrl from '~lib/common/utils/getSafeRedirectUrl';

const redirectToRequestedUrl = (redirectUrl) => {
  if (!redirectUrl) return false;
  window.location.replace(redirectUrl);
  return true;
};

/**
 * Invites an unaffiliated user into the public org for their account type.
 * Used for both the initial signup and the resend on the confirmation panel -
 * the service updates and re-sends an existing invite rather than duplicating
 * it.
 */
async function inviteUnaffiliated(user, accountType) {
  await userManagement.post('/invitations/invite-unaffiliated', {
    email: user.email,
    accountType,
  });
}

export default {
  name: 'login-form-container',
  components: { LoginForm },
  inheritAttrs: false,
  data() {
    return {
      loading: false,
      error: null,
      invited: false,
      pending: false,
    };
  },
  async created() {
    const { query } = this.$route;
    if (await auth.isAuthenticated()) {
      if (redirectToRequestedUrl(query.redirectUrl)) {
        return;
      }
      this.$router.push({ name: 'verifyEmail' });
    }
  },
  methods: {
    async login(email, password) {
      this.error = null;
      this.loading = true;
      const { query } = this.$route;
      try {
        // short circuit for missing password
        if (!password || !password.length) {
          throw new Error('Must specify a password.');
        }
        const options = {
          email,
          password,
        };
        if (query.redirectUrl) {
          const safeRedirectUrl = getSafeRedirectUrl(query.redirectUrl);
          if (safeRedirectUrl) {
            const callbackUrl = new URL('/auth/callback', window.location.origin);
            callbackUrl.searchParams.set('updated', Date.now().toString());
            callbackUrl.searchParams.set('redirectUrl', safeRedirectUrl);
            options.redirectUri = callbackUrl.toString();
          }
        }
        await auth.login(options);
      } catch (error) {
        console.error(error);
        this.error = {
          ...error,
          message: error.message || error.description,
        };
      } finally {
        this.loading = false;
      }
    },
    async signup(user, accountType) {
      this.error = null;
      this.pending = false;
      this.invited = false;
      this.loading = true;
      try {
        // An invite the user already has takes precedence - inviting them into
        // the public org on top of it is what we are trying to avoid.
        const result = await userManagement.post('invites/pending', { email: user.email });
        if (result.data && result.data.pending) {
          this.pending = true;
        } else {
          await inviteUnaffiliated(user, accountType);
          this.invited = true;
        }
      } catch (error) {
        console.error(error);
        this.error = { ...error, message: error.message || error.description };
      } finally {
        this.loading = false;
      }
    },
    async resend(user, accountType) {
      this.error = null;
      this.loading = true;
      try {
        await inviteUnaffiliated(user, accountType);
      } catch (error) {
        console.error(error);
        this.error = { ...error, message: error.message || error.description };
      } finally {
        this.loading = false;
      }
    },
    resetSignup() {
      this.error = null;
      this.invited = false;
      this.pending = false;
    },
  },
};
</script>
